import React from 'react';
import { useQuery } from 'react-query';
import { getNewAppVersion } from '@api/autoInsights';
import { useDispatch, useSelector } from 'react-redux';
import { track, getFilterFromRules } from '@utils';
import { metricKeys } from '@pulse/metrics';
import { AUTO_INSIGHTS_NEWAPPVERSION_CLICK } from '@src/utils/sensor/track';
import { createFilterRule , ALL_TRAFFIC } from '@utils/filter';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { DASHBOARD_TYPE, METRIC_NEWAPP_VERSION } from '@utils/constants';
import { drawerEvent } from '../AlertInsights/hooks';
import { formatInterval } from './formatter';
import { SettingTypes } from '../Settings';
import getGranularity from '@utils/granularity';
import { createTempDashboard } from '@src/store/tempDashboard/actions';

function useFetchSummary({ to, limit = 100 }) {
  return useQuery(
    [SettingTypes.appVersionPerf, to, limit],
    () =>
      getNewAppVersion({
        params: {
          to,
          limit,
        },
      }),
    {
      // default cacheTime is 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

function useOpenSummary({ closeMore }) {
  const dispatch = useDispatch();
  const dimensionsList = useSelector(allDimensionsListSelector);
  // Dimension is always set as default
  const dimensionName = METRIC_NEWAPP_VERSION;

  return (values) => {
    const newVersionRules = [
      createFilterRule(
        dimensionsList,
        'Device Name',
        values.newVersion.dimensions[dimensionName],
        values.newVersion.dimensions[dimensionName],
      ),
      createFilterRule(
        dimensionsList,
        'App Version',
        Object.values(values.newVersion.dimensions)[1],
        Object.values(values.newVersion.dimensions)[1],
      ),
    ];
    const newVersionFilter = getFilterFromRules(newVersionRules);

    const oldVersionRules = [
      createFilterRule(
        dimensionsList,
        'Device Name',
        values.oldVersion.dimensions[dimensionName],
        values.oldVersion.dimensions[dimensionName],
      ),
    ];

    // oldVersion appVersion can be empty
    if (Object.values(values.oldVersion.dimensions)[1]) {
      oldVersionRules.push(
        createFilterRule(
          dimensionsList,
          'App Version',
          Object.values(values.oldVersion.dimensions)[1],
          Object.values(values.oldVersion.dimensions)[1],
        ),
      );
    }
    const oldVersionFilter = getFilterFromRules(oldVersionRules);

    const interval = {
      from: formatInterval(values.interval.start),
      to: formatInterval(values.interval.end),
      shortcut: 'Custom',
      granularity: getGranularity(values.interval.start, values.interval.end),
    };

    const dashboardConfig = {
      // usecase key enforces the new drill-down workflow (without injector layer)
      usecaseKey: 'compare-dashboard',
      metricsChartContainer: {
        metric: metricKeys.SpiStreams,
        metrics: [
          metricKeys.SpiStreams,
          metricKeys.StreamingPerformanceIndex,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.VideoStartFailuresTech,
          metricKeys.HighStartupTime,
          metricKeys.HighRebuffering,
          metricKeys.LowBitrate,
          metricKeys.VideoPlaybackFailuresTech,
        ],
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        interval,
      },
      'module:trends': {
        base: {
          compareFilters: [newVersionFilter, oldVersionFilter],
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
      metricPanel: {
        size: 'm',
      },
    };

    dispatch(
      createTempDashboard(DASHBOARD_TYPE.TRENDS_DRILLDOWN, dashboardConfig, `Compare App Version`),
    );

    document.dispatchEvent(drawerEvent);
    closeMore();
    track({ event: AUTO_INSIGHTS_NEWAPPVERSION_CLICK });
  };
}

export { useFetchSummary, useOpenSummary };
